import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import AskIcon from 'shared/components/ui/AskIcon';
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, ChevronDown, Lock, Check, Search, X, Zap, Clock, Workflow, FlaskConical, ScrollText, Database, Bell, Plus } from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { listChatSessions } from "shared/services/chatService";
import ChatSearchDialog from "features/dashboard/components/ChatSearchDialog";

import { useParams } from "react-router-dom";
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import TraceDetailPage from 'features/agent-analytics/pages/TraceDetailPage';
import UxSignalDetailDrawer from 'features/agent-analytics/components/UxSignalDetailDrawer';
import { HighlightContext, HighlightText } from 'shared/components/HighlightText';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import { useSubscription } from "shared/context/subscriptionContext";
import { useTeam } from "shared/context/teamContext";
import axiosInstance from 'shared/services/api/axiosInstance';
import teamService from 'shared/services/teamService';
import preloadData from 'shared/utils/preloadService';
import { clearWorkspaceSetupPending, markWorkspaceSetupPending } from 'shared/utils/workspaceSetupPending';
import { invalidateAllChatCache } from 'shared/utils/chatCache';
import { isDemoTeamActive, exitDemoTeam } from 'shared/utils/demoTeam';
import notificationService, { invalidateUnreadCount } from 'shared/services/notificationService';
import { trackClientEvent, resetIdentity } from 'shared/utils/trodoEvents';
import { useBoards } from 'features/analytics/studio/hooks/useBoards';
import Icon from 'shared/components/Icon';
import { usePermissions } from 'shared/hooks/usePermissions';
import usePolling from 'shared/hooks/usePolling';
import { ISSUES_UI_ENABLED, LEGACY_STUDIO_UI } from 'shared/config/featureFlags';
import {
  visibleSettingsSections,
  resolveSettingsSection,
  settingsNavIsActive,
} from 'features/settings/settingsNavConfig';
import GlobalSearchModal from 'shared/components/search/GlobalSearchModal';
import WorkspaceSetupOverlay from 'features/onboarding/components/WorkspaceSetupOverlay';
import { isAnnotationDeepLink, isNumericTeamId, withTeamSlug } from 'features/agent-analytics/annotations/annotationDeepLink';
import { useAppDialog } from 'shared/context/AppDialogContext';

/** How many conversations the rail keeps to hand. The rest are searchable. */
const RAIL_CHAT_LIMIT = 10;

/** "2m", "3h", "1w" — the compact age used beside each chat in the Ask rail. */
function relativeShort(iso) {
  if (!iso) return '';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return 'now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  if (secs < 7 * 86400) return `${Math.floor(secs / 86400)}d`;
  return `${Math.floor(secs / (7 * 86400))}w`;
}

// Shared across Dashboard + AnalyticsLayout so collapse survives route changes (e.g. analytics → Settings).
const SIDEBAR_NAV_COLLAPSED_KEY = 'trodo.sidebarNavCollapsed';

function readSidebarNavCollapsed() {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(SIDEBAR_NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Lightweight right-side panel shown when an event-type result is clicked in
 *  the global search. `event.highlight` (the search query) marks its matches
 *  in the name and property values — the reason this panel opened. */
function SearchEventPanel({ event, teamId, onClose }) {
  const hl = (event?.highlight || '').trim();
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!event?.eventName || !teamId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/events', {
          params: { team_id: teamId, eventName: event.eventName, limit: 1 },
        });
        if (cancelled) return;
        const rows = res.data?.events || [];
        setInstance(rows[0] || null);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [event?.eventName, teamId]);

  const props = instance?.properties || {};
  const allKeys = Object.keys(props);
  const standardKeys = new Set(['distinct_id', 'event_id', 'occurred_at', 'event_name', 'event_type', 'team_id']);
  const customKeys = allKeys.filter((k) => !standardKeys.has(k));

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" style={{ pointerEvents: 'none' }}>
      <div
        className="relative h-full w-full max-w-md shadow-2xl flex flex-col"
        style={{ background: 'var(--surface-sunken)', borderLeft: '1px solid var(--border-default)', pointerEvents: 'auto' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: 'var(--border-default)' }}>
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-surface border border-[color:var(--border-default)]">
            <Zap className="w-4 h-4 text-[color:var(--text-muted)]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[color:var(--text-primary)] truncate">
              <HighlightContext.Provider value={hl}><HighlightText text={event.eventName} /></HighlightContext.Provider>
            </div>
            {event.summary?.count != null && (
              <div className="text-[11px] text-[color:var(--text-muted)]">{event.summary.count.toLocaleString()} occurrences</div>
            )}
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 hover:bg-surface-hover text-[color:var(--text-muted)]">
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading ? (
            <div className="text-sm text-[color:var(--text-muted)] py-8 text-center">Loading…</div>
          ) : !instance ? (
            <div className="text-sm text-[color:var(--text-muted)] py-8 text-center">No recent events found for <strong>{event.eventName}</strong>.</div>
          ) : (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Most recent occurrence</div>
              {instance.occurred_at && (
                <div className="text-[11px] text-[color:var(--text-muted)] mb-3">
                  {new Date(instance.occurred_at).toLocaleString()}
                  {instance.distinct_id ? <span className="ml-2 text-[color:var(--text-muted)]">· {instance.distinct_id}</span> : null}
                </div>
              )}
              {customKeys.length === 0 ? (
                <div className="text-sm text-[color:var(--text-muted)]">No custom properties.</div>
              ) : (
                <div className="rounded-xl border border-[color:var(--border-default)] overflow-hidden bg-surface">
                  {customKeys.map((k, i) => (
                    <div key={k} className={`flex items-start gap-3 px-3 py-2 ${i > 0 ? 'border-t border-[color:var(--surface-hover)]' : ''}`}>
                      <span className="text-[11px] font-medium text-[color:var(--text-muted)] w-32 shrink-0 pt-0.5 break-all">{k}</span>
                      <span className="text-[11px] text-[color:var(--text-primary)] break-all">
                        <HighlightContext.Provider value={hl}>
                          <HighlightText text={typeof props[k] === 'object' ? JSON.stringify(props[k]) : String(props[k] ?? '')} />
                        </HighlightContext.Provider>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {/* Footer */}
        <div className="px-5 py-3 border-t text-[11px] text-[color:var(--text-muted)]" style={{ borderColor: 'var(--border-default)' }}>
          Event type · {event.eventName}
        </div>
      </div>
    </div>
  );
}

/**
 * Placeholder for a nav section while entitlements are still loading.
 *
 * Deliberately not the "locked" row and not the sub-items: neither is true
 * yet, since we don't know the answer. A neutral animate-pulse /
 * bg-surface-hover row, so it reads as "loading" rather than either "here's
 * your content" or "you don't have access".
 */
function NavSectionSkeleton({ rows, showExpanded }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className={`flex items-center gap-2.5 py-1 ${!showExpanded ? "px-2 justify-center w-full" : "pl-3 pr-2 ml-1 mr-3"}`}
        >
          <div className="h-4 w-4 shrink-0 rounded bg-surface-hover animate-pulse" />
          {showExpanded && (
            <div
              className="h-3 rounded bg-surface-hover animate-pulse"
              style={{ width: `${60 + (i % 3) * 10}%` }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

const Sidebar = ({ isOpen, onClose, onNavigate, hideMarketing, isCompact, currentPage }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => readSidebarNavCollapsed());

  const setCollapsedPersist = useCallback((collapsed) => {
    setIsCollapsed(collapsed);
    try {
      localStorage.setItem(SIDEBAR_NAV_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }, []);
  const params = useParams();
  const location = useLocation();
  const { can: canPermission } = usePermissions();
  const { boards: analyticsBoards, loadBoards, createBoard } = useBoards();
  const isInAnalyticsBoards = location.pathname.includes('/analytics/boards');
  const isInAnalytics = location.pathname.includes('/analytics/');
  const currentBoardIdFromPath = (() => {
    const m = location.pathname.match(/\/analytics\/boards\/([^/]+)/);
    return m ? m[1] : null;
  })();
  const navigate = useNavigate();
  const [selectedTeam, setSelectedTeam] = useState(localStorage.getItem('selectedTeam') || 'defaultTeam');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { isActive: hasActiveSubscription, canAccessPage, loading: entitlementsLoading } = useSubscription();
  const { setSelectedTeam: setSelectedTeamContext } = useTeam();
  // Team selector state
  const [curTeams, setCurTeams] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [teamMenuLayout, setTeamMenuLayout] = useState(null);
  const [workspaceSubmenuOpen, setWorkspaceSubmenuOpen] = useState(false);
  /**
   * "This comment is in another workspace — switch?"
   *
   * Held in sessionStorage, not component state, because the effect that
   * raises it also NAVIGATES: it sends you to your current team's chat so you
   * are not left on a page rendering the wrong workspace's data. That
   * navigation swaps layouts, the sidebar remounts, and plain state died with
   * it — so following a notification link silently dropped you on your own
   * home page with nothing to say the comment was elsewhere. Which is exactly
   * what a Slack link did.
   *
   * sessionStorage also survives the cold page load a real link click is.
   */
  const PENDING_SWITCH_KEY = 'trodo.pendingTeamSwitch';
  const [pendingTeamSwitch, setPendingTeamSwitchState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(PENDING_SWITCH_KEY) || 'null'); } catch { return null; }
  });
  const setPendingTeamSwitch = useCallback((value) => {
    setPendingTeamSwitchState(value);
    try {
      if (value) sessionStorage.setItem(PENDING_SWITCH_KEY, JSON.stringify(value));
      else sessionStorage.removeItem(PENDING_SWITCH_KEY);
    } catch { /* the prompt still works for this render */ }
  }, []);

  const dropdownRef = useRef(null);
  const teamMenuPortalRef = useRef(null);
  const teamWorkspaceSubmenuRef = useRef(null);
  const teamsRequestRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  

  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [searchPanelRun, setSearchPanelRun] = useState(null);   // { runId, query }
  const [searchPanelSpan, setSearchPanelSpan] = useState(null); // { spanId, query }
  const [searchPanelEvent, setSearchPanelEvent] = useState(null); // { eventName, summary, highlight }
  const [searchPanelUxSignal, setSearchPanelUxSignal] = useState(null); // ux signal id
  const searchPanelTeamId = getSelectedTeamNumericId();
  
  // Control sidebar expansion state - now based on collapse button instead of hover
  const showExpanded = !isCollapsed;
  /** Sized to pair with primary nav labels */
  const navIconPx = !showExpanded ? 19 : 18;

  // On settings page, primary sidebar collapses by default (controlled by Dashboard); do not force visible
  const forceVisible = false;
  
  // Extract the current page from the URL path
  const path = location.pathname;
  const pathSegments = path.split('/').filter(segment => segment);
  const pathCurrentPage = pathSegments.length > 1 ? pathSegments[1] : pathSegments[0] || 'dashboard';

  const rawTeamSlug =
    selectedTeam ||
    params.team ||
    (typeof window !== 'undefined' ? localStorage.getItem('selectedTeam') : '') ||
    'team1';
  const teamBasePath = `/${encodeURIComponent(rawTeamSlug)}`;

  const isSettingsShell = location.pathname.includes('/settings');
  /**
   * Agent mode gives the Ask page its own rail, the way Settings does: Back to
   * app, New chat, Agents, then the conversations. Same design as the app rail,
   * different items — in that mode the page is a different place.
   */
  const isChatPage = /\/chat(\/|$)/.test(location.pathname);
  // Agents live inside agent mode now, not in the app's own nav — so the
  // agent pages wear this rail too, and going to one from here keeps you in
  // the mode rather than dropping you back into the app with a link.
  const isAgentsPage = /\/agents(\/|$)/.test(location.pathname) && !location.pathname.includes('/agent-analytics');
  // Ask is an ordinary page in the app by default; it asks for this rail with
  // ?agent=1 (the toggle lives in the chat page's title bar).
  const isAskShell = (isChatPage && new URLSearchParams(location.search).get('agent') === '1') || isAgentsPage;
  const activeSessionId = isChatPage ? new URLSearchParams(location.search).get('session') : null;
  const askShellPath = (sessionId) =>
    `${teamBasePath}/chat?agent=1${sessionId ? `&session=${encodeURIComponent(sessionId)}` : ''}`;
  const [askSessions, setAskSessions] = useState([]);
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  useEffect(() => {
    if (!isAskShell) return undefined;
    let askTeamId = null;
    try { askTeamId = JSON.parse(localStorage.getItem('selectedTeamData'))?.id ?? null; } catch (_) { /* no team yet */ }
    if (!askTeamId) return undefined;
    let cancelled = false;
    // Ten, for quick access. Everything else is behind the search beside the
    // "Chats" heading — loading the whole history to draw a sidebar got slower
    // with every conversation and still meant scrolling to find one.
    listChatSessions(askTeamId, { limit: RAIL_CHAT_LIMIT, offset: 0 })
      .then((r) => { if (!cancelled) setAskSessions(r?.sessions || []); })
      .catch(() => {});
    return () => { cancelled = true; };
    // Re-read when the active session changes: a first message creates one.
  }, [isAskShell, activeSessionId, location.pathname]);
  // Where "Back to app" goes: the last non-shell page you were on.
  useEffect(() => {
    if (isAskShell || isSettingsShell) return;
    try { sessionStorage.setItem('trodo.lastAppPath', location.pathname + location.search); } catch (_) { /* private mode */ }
  }, [location.pathname, location.search, isAskShell, isSettingsShell]);
  const settingsSection = isSettingsShell ? resolveSettingsSection(location.pathname) : null;
  const publicAsset = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

  // Define free pages (that don't need subscription)
  const freePages = ['dashboard', 'settings'];
  
  // Handle collapse/expand toggle
  const toggleCollapse = () => {
    setCollapsedPersist(!isCollapsed);
  };

  // Team selector functions
  // The paid tier is called Pro. "Growth" was its previous name and several
  // legacy plan strings still normalise to it internally (see
  // normalizeCanonicalPlanType) — those are storage values, not something to
  // show a customer. Mapping them all to "Pro" here means a converted team and
  // a not-yet-converted one read the same, which is what they experience.
  const getPlanDisplayName = (subscription) => {
    if (!subscription || !subscription.plan) {
      return null;
    }
    const p = subscription.plan.toLowerCase();
    if (['starter', 'pro', 'advance', 'professional', 'growth'].includes(p)) return 'Pro';
    if (p === 'trial' || p === 'trialing') return 'Pro trial';
    return subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1);
  };

  const fetchCompleteTeamData = async (teamId) => {
    try {
      return await teamService.fetchTeamData(teamId);
    } catch (error) {
      console.error('Error fetching complete team data:', error);
      return null;
    }
  };

  const fetchSubscriptionData = async (teamId) => {
    try {
      return await teamService.fetchSubscriptionData(teamId);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      return null;
    }
  };

  const goToNewProjectPage = () => {
    trackClientEvent('new_project_clicked', { from: 'sidebar' }, { category: 'workspace' });
    setWorkspaceSubmenuOpen(false);
    setDropdownOpen(false);
    navigate(`${teamBasePath}/new-project`);
  };

  const handleTeamSelect = async (teamss, options = {}) => {
    const previousTeam = localStorage.getItem('selectedTeam');
    if (previousTeam !== teamss.name) {
      setIsRefreshing(true);
      markWorkspaceSetupPending();

      trackClientEvent(
        'team_switched_ui',
        {
          to_team_id: String(teamss._id ?? teamss.id ?? ''),
          to_team_name: teamss.name ?? null,
          from_team_name: previousTeam || null,
        },
        { category: 'workspace' }
      );

      try {
        localStorage.setItem('selectedTeam', teamss.name);
        setSelectedTeam(teamss.name);
        
        const teamDataToStore = {
          _id: teamss._id,
          name: teamss.name,
          role: teamss.role || 'member',
          isOwner: teamss.isOwner || false
        };
        
        localStorage.setItem('selectedTeamData', JSON.stringify(teamDataToStore));
        
        // Tell backend which team should be the user's default for authenticated APIs
        try {
          const activeTeamId = teamss._id || teamss.id;
          if (activeTeamId) {
            await axiosInstance.post('/team/user/default-team', {
              teamId: activeTeamId
            });
          }
        } catch (err) {
          console.error('Error setting default team on backend during team switch:', err);
        }
        
        window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
          detail: { teamData: teamDataToStore }
        }));
        
        localStorage.removeItem("selectedWebsite");
        localStorage.removeItem("idy");
        sessionStorage.removeItem("preloadedWebsites");
        sessionStorage.removeItem("preloadedContracts");
        
        const fetchCompleteData = async () => {
          try {
            const [completeTeamData, subscriptionData] = await Promise.all([
              fetchCompleteTeamData(teamss._id),
              fetchSubscriptionData(teamss._id)
            ]);

            const enhancedTeamData = {
              ...teamDataToStore,
              ...(completeTeamData || {}),
              subscription: subscriptionData?.subscription || null
            };

            localStorage.setItem('selectedTeamData', JSON.stringify(enhancedTeamData));

            window.dispatchEvent(new CustomEvent('teamDataUpdated', {
              detail: { teamData: enhancedTeamData }
            }));
          } catch (error) {
            console.error('Error fetching complete team data:', error);
          }
        };

        await Promise.all([
          fetchCompleteData(),
          preloadData(true, teamss.name),
        ]);

        // Stay on the requested annotation when a Slack/Inbox deep link asked
        // us to switch teams; otherwise land on Ask for the new workspace.
        if (options.nextPath) {
          navigate(options.nextPath, { replace: true });
        } else {
          const encodedTeam = encodeURIComponent(teamss.name);
          navigate(`/${encodedTeam}/chat`, { replace: true });
        }
      } catch (error) {
        console.error("Error during team switch:", error);
        clearWorkspaceSetupPending();
        localStorage.setItem('selectedTeam', previousTeam);
        setSelectedTeam(previousTeam);
      } finally {
        setIsRefreshing(false);
      }
    }
    
    setWorkspaceSubmenuOpen(false);
    setDropdownOpen(false);
  };

  /**
   * Asked through the app's own dialog, not a bespoke one.
   *
   * This was a hand-rolled modal, and that is the actual problem with how
   * these read: the app has ~80 hand-rolled dialogs and this one had its own
   * radius (8px vs the shell's 12), its own title size (14 vs 15), its own
   * button padding and its own button radius (6 vs 8). Each is plausible on
   * its own and none of them match, so a dialog looks assembled rather than
   * part of anything. useAppDialog is the shell every other dialog uses, so
   * this one now inherits the same geometry for free — and there is one fewer
   * variant to drift.
   */
  const { confirm: confirmDialog } = useAppDialog();

  useEffect(() => {
    if (!pendingTeamSwitch) return;
    const { teamName, nextPath } = pendingTeamSwitch;
    const isMember = (curTeams || []).some((t) => t.name === teamName);
    // Wait for the team list before asking — "you cannot open this" is a
    // wrong and alarming thing to say while the answer is still loading.
    if (!isMember && !(curTeams || []).length) return;
    // Clearing the pending state re-runs this effect, so an `if (cancelled)`
    // guard on the promise would discard the very click it is waiting for —
    // the cleanup runs long before the person answers. Once the dialog is
    // open, their answer is acted on: it is an explicit choice, not a stale
    // render.
    setPendingTeamSwitch(null);
    confirmDialog(
      isMember
        ? `This comment is in ${teamName}. Switch to that workspace to open it.`
        : `This comment is in ${teamName}, which this account cannot open. Sign in with an account that has access and you will land straight on it.`,
      {
        title: isMember ? 'Open in another workspace' : 'No access to this workspace',
        confirmLabel: isMember ? `Switch to ${teamName}` : 'Sign in as someone else',
        cancelLabel: 'Stay here',
      },
    ).then((ok) => {
      if (!ok) return;
      if (isMember) {
        const match = (curTeams || []).find((t) => t.name === teamName);
        if (match) handleTeamSelect(match, { nextPath });
        return;
      }
      // Keep the destination so the right account lands on the comment.
      ['accessToken', 'token', 'User', 'selectedTeam', 'selectedTeamData'].forEach((k) => {
        try { localStorage.removeItem(k); } catch { /* best effort */ }
      });
      try { sessionStorage.removeItem('userTeams'); } catch { /* best effort */ }
      navigate(`/login?next=${encodeURIComponent(nextPath)}`, { replace: true });
    });
    return undefined;
  }, [pendingTeamSwitch, curTeams, confirmDialog, handleTeamSelect, navigate, setPendingTeamSwitch]);

  const handleLogout = async (event) => {
    event.preventDefault();

    // Fire log_out_clicked + reset Trodo identity BEFORE clearing localStorage —
    // otherwise the next user inherits this user's distinct id (skill pitfall #1).
    await trackClientEvent('log_out_clicked', {}, { category: 'auth' });
    await resetIdentity();

    try {
      // Get the access token to use as a fallback authentication method
      const accessToken = localStorage.getItem('accessToken');

      // Call the logout API endpoint to invalidate refresh token
      await axiosInstance.post('/auth/logout', {}, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      
      // Clear all auth-related items from localStorage
      try { window.dispatchEvent(new Event('trodo:session-cleared')); } catch (_) {}
      localStorage.removeItem('accessToken');
      localStorage.removeItem('selectedTeam');
      localStorage.removeItem('selectedTeamData');
      localStorage.removeItem('selectedWebsite');
      localStorage.removeItem('User');
      localStorage.removeItem('idy');
      localStorage.removeItem('subscriptionStatus');
      
      // Clear sessionStorage
      sessionStorage.removeItem('subscriptionStatus');

      invalidateAllChatCache();
      
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      
      // Even if API call fails, still clear localStorage and sessionStorage, then redirect to be safe
      try { window.dispatchEvent(new Event('trodo:session-cleared')); } catch (_) {}
      localStorage.removeItem('accessToken');
      localStorage.removeItem('selectedTeam');
      localStorage.removeItem('selectedTeamData');
      localStorage.removeItem('selectedWebsite');
      localStorage.removeItem('User');
      localStorage.removeItem('idy');
      localStorage.removeItem('subscriptionStatus');
      
      // Clear sessionStorage
      sessionStorage.removeItem('subscriptionStatus');

      invalidateAllChatCache();
      
      navigate('/login');
    }
  };
  
  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      const now = Date.now();
      
      // Check sessionStorage cache first
      const cached = sessionStorage.getItem('userTeams');
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 5 minutes old
          if (Date.now() - timestamp < 5 * 60 * 1000) {
            setCurTeams(data);
            lastFetchTimeRef.current = timestamp;
            return;
          }
        } catch (e) {
          // Failed to parse cached teams
        }
      }
      
      if (isLoadingTeams || (now - lastFetchTimeRef.current < 30000)) {
        return;
      }
      
      try {
        setIsLoadingTeams(true);
        lastFetchTimeRef.current = now;
        
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get('/team/details', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const teams = response.data.team;
        setCurTeams(teams);
        
        // Sync selectedTeam name with actual team data if it changed
        const currentSelectedTeamName = localStorage.getItem('selectedTeam');
        const currentTeamData = localStorage.getItem('selectedTeamData');
        if (currentTeamData) {
          try {
            const parsed = JSON.parse(currentTeamData);
            // Find the team in the fetched teams list by ID
            const matchingTeam = teams.find(t => 
              (t._id && parsed._id && t._id.toString() === parsed._id.toString()) ||
              (t.id && parsed.id && t.id.toString() === parsed.id.toString())
            );
            
            // If team name changed, update it
            if (matchingTeam && matchingTeam.name && matchingTeam.name !== currentSelectedTeamName) {
              localStorage.setItem('selectedTeam', matchingTeam.name);
              setSelectedTeam(matchingTeam.name);
            }
          } catch (e) {
            console.error('Error syncing team name:', e);
          }
        }
        
        // Cache the teams data
        sessionStorage.setItem('userTeams', JSON.stringify({
          data: teams,
          timestamp: now
        }));
      } catch (error) {
        console.error("Error fetching teams:", error);
      } finally {
        setIsLoadingTeams(false);
      }
    };

    fetchTeams();
    
    return () => {
      if (teamsRequestRef.current) {
        clearTimeout(teamsRequestRef.current);
      }
    };
  }, []);

  // Listen for team data updates (e.g., after team switch or creation)
  useEffect(() => {
    const handleTeamDataUpdate = (event) => {
      const updatedTeamData = event.detail?.teamData;
      if (!updatedTeamData) return;

      const currentSelectedTeam = localStorage.getItem('selectedTeam');
      const currentTeamData = localStorage.getItem('selectedTeamData');
      
      // Check if this is the currently selected team by comparing IDs
      let isCurrentTeam = false;
      if (currentTeamData) {
        try {
          const parsed = JSON.parse(currentTeamData);
          isCurrentTeam = parsed._id === updatedTeamData._id || 
                         parsed.id === updatedTeamData.id ||
                         (updatedTeamData._id && parsed._id === updatedTeamData._id) ||
                         (updatedTeamData.id && parsed.id === updatedTeamData.id);
        } catch (e) {
          console.error('Error parsing team data:', e);
        }
      }

      // If team name changed, update the selected team
      const newTeamName = updatedTeamData.name || updatedTeamData.onboarding?.businessName;
      if (isCurrentTeam && newTeamName && newTeamName !== currentSelectedTeam) {
        localStorage.setItem('selectedTeam', newTeamName);
        setSelectedTeam(newTeamName);
        
        // Update selectedTeamData with the new data
        localStorage.setItem('selectedTeamData', JSON.stringify({
          ...updatedTeamData,
          _lastFetched: Date.now()
        }));
        
        // Refresh teams list to get updated names
        sessionStorage.removeItem('userTeams');
      }
    };

    window.addEventListener('teamDataUpdated', handleTeamDataUpdate);
    return () => {
      window.removeEventListener('teamDataUpdated', handleTeamDataUpdate);
    };
  }, []);

  // Fetch teams when dropdown opens - always refresh to get latest teams
  useEffect(() => {
    const fetchTeamsIfNeeded = async () => {
      if (dropdownOpen && !isLoadingTeams) {
        const now = Date.now();
        
        // Always fetch fresh data when dropdown opens (ignore cache and length check)
        // Only throttle if we've fetched very recently (within 2 seconds)
        if (now - lastFetchTimeRef.current > 2000) {
          try {
            setIsLoadingTeams(true);
            lastFetchTimeRef.current = now;
            
            // Clear cache to force fresh fetch
            sessionStorage.removeItem('userTeams');
            
            const token = localStorage.getItem("token");
            const response = await axiosInstance.get('/team/details', {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            const teams = response.data.team;
            
            setCurTeams(teams);
            
            // Cache the teams data
            sessionStorage.setItem('userTeams', JSON.stringify({
              data: teams,
              timestamp: now
            }));
          } catch (error) {
            console.error("Error fetching teams:", error);
          } finally {
            setIsLoadingTeams(false);
          }
        }
      }
    };
    
    fetchTeamsIfNeeded();
  }, [dropdownOpen, isLoadingTeams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownOpen &&
        !dropdownRef.current?.contains(event.target) &&
        !teamMenuPortalRef.current?.contains(event.target) &&
        !teamWorkspaceSubmenuRef.current?.contains(event.target)
      ) {
        setWorkspaceSubmenuOpen(false);
        setDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [location.pathname, dropdownOpen]);

  useEffect(() => {
    setDropdownOpen(false);
    setWorkspaceSubmenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!dropdownOpen) setWorkspaceSubmenuOpen(false);
  }, [dropdownOpen]);

  // Account menu + optional workspace submenu: portaled to body; nested panel opens to the right.
  useLayoutEffect(() => {
    if (!dropdownOpen) {
      setTeamMenuLayout(null);
      return;
    }
    const compute = () => {
      const root = dropdownRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const margin = 8;
      const mainWidth = 260;
      let left = Math.max(margin, Math.min(rect.left, window.innerWidth - mainWidth - margin));
      const top = Math.min(rect.bottom + 4, window.innerHeight - margin);
      const main = {
        position: 'fixed',
        top,
        left,
        width: mainWidth,
        maxHeight: 'min(70vh, calc(100vh - 16px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: 9999,
        pointerEvents: 'auto',
      };

      let workspace = null;
      if (workspaceSubmenuOpen && teamMenuPortalRef.current) {
        const r = teamMenuPortalRef.current.getBoundingClientRect();
        const subW = 260;
        const gap = 4;
        let subLeft = r.right + gap;
        if (subLeft + subW > window.innerWidth - margin) {
          subLeft = r.left - subW - gap;
        }
        subLeft = Math.max(margin, Math.min(subLeft, window.innerWidth - subW - margin));
        workspace = {
          position: 'fixed',
          top: r.top,
          left: subLeft,
          width: subW,
          maxHeight: 'min(70vh, calc(100vh - 16px))',
          overflowY: 'auto',
          overflowX: 'hidden',
          zIndex: 10000,
          pointerEvents: 'auto',
        };
      }

      setTeamMenuLayout({ main, workspace });
    };
    compute();
    const id = requestAnimationFrame(compute);
    window.addEventListener('resize', compute);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', compute);
    };
  }, [dropdownOpen, workspaceSubmenuOpen]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (!token) return;
      const result = await notificationService.getUnreadCount();
      setUnreadNotifications(result?.unread || 0);
    } catch (_) {
      /* silent for sidebar badge */
    }
  }, []);

  // Unread badge. Notifications are rare, so this is a slow background check,
  // not a feed: every 2 min while the tab is in view, doubling to 10 min while
  // nothing happens, never in a background tab. Coming back after 30 s+ away
  // reads it once (at most once a minute); a window whose focus flickers is
  // not coming back. Inbox actions refresh it right away (below).
  usePolling(fetchUnreadCount, {
    interval: 120_000, maxInterval: 600_000, focusMinGap: 60_000, minAway: 30_000,
  });
  useEffect(() => {
    // Bypass the cache, don't just re-ask: the event means the count has
    // already changed, and the service only self-invalidates for writes that
    // went through it. Anything else that dispatches this still gets a fresh
    // number rather than the one from a moment ago.
    const onInboxRefresh = () => { invalidateUnreadCount(); fetchUnreadCount(); };
    window.addEventListener('trodo:inbox-unread-refresh', onInboxRefresh);
    return () => window.removeEventListener('trodo:inbox-unread-refresh', onInboxRefresh);
  }, [fetchUnreadCount]);
  
  useEffect(() => {
    const storedTeam = localStorage.getItem('selectedTeam') || 'team1';
    const urlTeam = params.team ? decodeURIComponent(params.team) : '';

    if (urlTeam && urlTeam !== storedTeam) {
      const annotationLink = isAnnotationDeepLink(location.pathname, location.search);
      if (annotationLink && !isNumericTeamId(urlTeam)) {
        const nextPath = withTeamSlug(`${location.pathname}${location.search}`, urlTeam);
        setPendingTeamSwitch({ teamName: urlTeam, nextPath });
        navigate(`/${encodeURIComponent(storedTeam)}/chat`, { replace: true });
      } else if (annotationLink && isNumericTeamId(urlTeam)) {
        navigate(`/${encodeURIComponent(storedTeam)}/chat`, { replace: true });
      } else {
        const tail = location.pathname.replace(/^\/[^/]+/, '') || '';
        const target = `/${encodeURIComponent(storedTeam)}${tail}`;
        navigate(target, { replace: true });
      }
    }

    setSelectedTeam(storedTeam);
  }, [params.team, navigate, location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && e.target.closest('aside') === null && window.innerWidth < 768) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);
  
  // Helper function to determine if a menu item is active
  const isActive = (page) => pathCurrentPage === page;
  
  const handleTeamSelectorClick = (e) => {
    e?.stopPropagation();
    if (isCollapsed) {
      setCollapsedPersist(false);
      setDropdownOpen(true);
      return;
    }
    setDropdownOpen((open) => !open);
  };

  // Only load the boards list when the user is actually in analytics.
  //
  // This used to also fire whenever the user had ANY pinned board, on every
  // page in the app — a leftover from when pinned boards rendered in the side
  // nav. They no longer do: boards live exclusively in Reports. That condition
  // cost two round trips on every page load (GET /boards/pinned, then the full
  // boards list) to render nothing. The only other consumer is
  // GlobalSearchModal, which is given loadBoards and fetches when it opens.
  useEffect(() => {
    if (isInAnalytics && loadBoards) loadBoards();
  }, [isInAnalytics, loadBoards]);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Global search: Ctrl/Cmd+K (main app shell only)
  useEffect(() => {
    if (isSettingsShell) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = String(e.key || '').toLowerCase();
      // Ctrl/Cmd+K stays search. Ask gets its own key rather than sharing one:
      // "find a thing" and "ask a question about this screen" are different
      // intentions and both are used constantly.
      if (key === 'k') {
        e.preventDefault();
        setGlobalSearchOpen(true);
      } else if (key === 'i') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('trodo:ask:toggle'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSettingsShell]);

  // Helper function to render navigation item with subscription-based access control
  const renderNavItem = (to, pageName, icon, label, alwaysAccessible = false) => {
    const isActiveItem = isActive(pageName);
    const hasAccess = alwaysAccessible || canAccessPage(pageName);
    
    // Define classes based on active state and access
    let itemClasses = isActiveItem
      ? "font-medium text-[color:var(--text-primary)] bg-surface-hover"
      : "text-[color:var(--text-primary)]";
    
    const iconStroke = 1.2;
    const iconWithColor = React.cloneElement(icon, {
      color: "currentColor",
      style: {
        strokeWidth: iconStroke,
        ...icon.props.style,
      },
    });

    // If user doesn't have access, show as locked
    if (!hasAccess) {
      itemClasses += " opacity-50 cursor-not-allowed";

      const lockedIcon = React.cloneElement(icon, {
        color: "currentColor",
        style: {
          strokeWidth: iconStroke,
          ...icon.props.style,
        },
      });

      return (
        <div 
          className={`${itemClasses} sidebar-nav-item sidebar-nav-item--locked flex items-center gap-2.5 ${!showExpanded ? "py-0.5 px-2 justify-center" : "py-1 pl-3 pr-2 ml-1 mr-3 w-auto rounded-lg"}`}
          title={`Upgrade your plan to access ${label}`}
        >
          <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">{lockedIcon}</span>
          {showExpanded && (
            <div className="flex items-center justify-between w-full">
              <span className="transition-opacity duration-150 ease-out">{label}</span>
              <Lock size={12} strokeWidth={1.25} className="sidebar-nav-icon-slot ml-1" />
            </div>
          )}
          {!showExpanded && (
            <div className="absolute -right-1 -top-1">
              <Lock size={11} strokeWidth={1.25} className="sidebar-nav-icon-slot" />
            </div>
          )}
        </div>
      );
    }
    
    // User has access - render normal clickable nav item
    return (
      <Link 
        to={to}
        className={`${itemClasses} sidebar-nav-item flex items-center gap-2.5 hover:bg-surface-hover ${!showExpanded ? "py-0.5 px-2 justify-center" : "py-1 pl-3 pr-2 ml-1 mr-3 rounded-lg"} cursor-pointer`}
        style={{ 
          willChange: 'background-color, color',
          ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' })
        }}
        onClick={() => onNavigate && onNavigate(pageName)}
      >
        <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">{iconWithColor}</span>
        {showExpanded && (
          <div className="flex items-center justify-between w-full">
            <span className="transition-opacity duration-150 ease-out" style={{ willChange: 'opacity' }}>{label}</span>
          </div>
        )}
      </Link>
    );
  };

  // One width rule for md+ — avoids md:w-* vs md:w-[48px] fighting and animating the rail open when popovers mount.
  const asideDesktopWidth = showExpanded
    ? "md:w-48 lg:w-52"
    : "md:w-[48px] md:min-w-[48px] md:max-w-[48px] md:overflow-x-visible pr-0";

  return (
    <>
    <aside
      style={{
        boxSizing: 'border-box',
        paddingRight: 0,
        marginRight: 0,
        borderRightWidth: 0,
        willChange: 'transform, width',
        backfaceVisibility: 'hidden',
        perspective: '1000px',
        borderRadius: '0px'
      }}
      className={`fixed md:relative sidebar-rail sidebar-optimized sidebar-transition ${!showExpanded ? "py-0.5 px-0.5" : "py-1 pl-1 pr-0"} flex flex-col h-screen transform ${currentPage === "settings" ? "z-10" : "z-50"} ${
        isOpen || forceVisible
          ? `translate-x-0 w-[85vw] max-w-[320px] sm:w-60 sm:max-w-none top-0 left-0 md:relative md:top-auto md:left-auto ${asideDesktopWidth}`
          : `-translate-x-full md:translate-x-0 ${asideDesktopWidth}`
      }`}
    >
      {/* Top row: Trodo logo (expanded) OR team initial (collapsed) + team name + chevron */}
      <div
        className={`relative mt-1 mb-1.5 flex min-w-0 items-center gap-1.5 ${
          showExpanded ? 'ml-1.5 mr-3' : 'mx-0 flex-col justify-center gap-1 px-0.5'
        }`}
      >
        {/* Brand mark: shown only when sidebar is expanded */}
        {showExpanded && (
          <img
            src={publicAsset('/trodologo.svg')}
            alt="Trodo"
            className="h-[18px] w-auto shrink-0 opacity-90 pointer-events-none select-none ml-1"
          />
        )}

        <div className={`flex min-w-0 items-center ${showExpanded ? 'flex-1' : ''}`}>
          <button
            ref={dropdownRef}
            type="button"
            onClick={handleTeamSelectorClick}
            className={
              showExpanded
                ? `group flex min-w-0 flex-1 items-center gap-1.5 rounded-lg px-1.5 py-1 text-left text-[13px] font-[450] leading-snug text-[color:var(--text-primary)] transition-colors duration-200 hover:bg-surface-hover/90 ${
                    dropdownOpen || location.pathname.includes('/settings') || location.pathname.includes('/integrations') || location.pathname.includes('/inbox')
                      ? 'bg-surface-hover/90'
                      : 'bg-transparent'
                  }`
                : `flex shrink-0 items-center justify-center rounded-lg p-1 transition-colors duration-200 hover:bg-surface-hover/90 ${
                    dropdownOpen || location.pathname.includes('/settings') || location.pathname.includes('/integrations') || location.pathname.includes('/inbox')
                      ? 'bg-surface-hover/90'
                      : 'bg-transparent'
                  }`
            }
            disabled={isRefreshing}
            title={selectedTeam || 'Team'}
            // Collapsed, this button renders only the brand mark — no text and
            // no alt — so name it explicitly rather than leaning on `title`.
            aria-label={`${selectedTeam || 'Team'}: switch workspace`}
          >
            {isRefreshing ? (
              showExpanded ? (
                <>
                  <span className="min-w-0 flex-1 truncate text-[12px] text-[color:var(--text-muted)]">Refreshing...</span>
                  <div className="h-2 w-2 shrink-0 rounded-full bg-info animate-pulse" />
                </>
              ) : (
                <div className="flex h-[18px] w-[18px] items-center justify-center rounded-[5px] bg-[color:var(--surface-hover)]">
                  <span className="text-[10px] font-medium leading-none text-[color:var(--text-primary)]">R</span>
                </div>
              )
            ) : (
              <>
                {/* Collapsed: the brand mark stands in for the full logo row.
                    This used to be a team-initial chip on a hardcoded pale
                    green, which read as an empty box: the initial was
                    --text-primary, i.e. near-white in dark mode, on a light
                    green fill. The team name still reaches the user via the
                    button's title/aria-label. */}
                {!showExpanded && (
                  <img
                    src={publicAsset('/trodologo.svg')}
                    alt=""
                    aria-hidden
                    className="h-[18px] w-[18px] shrink-0 select-none object-contain"
                  />
                )}
                {showExpanded && (
                  <>
                    <span className="min-w-0 flex-1 truncate text-left">{selectedTeam}</span>
                    <ChevronDown
                      size={14}
                      strokeWidth={1.5}
                      className={`ml-auto shrink-0 text-[color:var(--text-primary)] opacity-75 transition-all duration-200 group-hover:opacity-100 ${dropdownOpen ? 'rotate-180' : ''}`}
                      aria-hidden
                    />
                  </>
                )}
              </>
            )}
          </button>
        </div>
      </div>

        {dropdownOpen && teamMenuLayout?.main && typeof document !== 'undefined'
          ? createPortal(
              <>
                <div
                  ref={teamMenuPortalRef}
                  className="sidebar-rail-portal scrollbar-thin overflow-hidden rounded-lg border border-line/90 bg-surface shadow-[0_8px_30px_-8px_rgba(15,23,42,0.1)]"
                  style={teamMenuLayout.main}
                >
                  <nav className="py-0.5" aria-label="Account menu">
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`${teamBasePath}/inbox`);
                        setWorkspaceSubmenuOpen(false);
                        setDropdownOpen(false);
                      }}
                      className="flex w-full items-center justify-between gap-2 px-3 py-1 text-left text-[13px] font-[450] leading-[19.5px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-sunken rounded-lg"
                    >
                      <span>Inbox</span>
                      {unreadNotifications > 0 ? (
                        <span className="min-w-[20px] rounded-full bg-brand px-1.5 py-0.5 text-center text-[11px] font-semibold leading-none text-ink-onBrand">
                          {unreadNotifications > 99 ? '99+' : unreadNotifications}
                        </span>
                      ) : null}
                    </button>
                    {/* Settings moved to the sidebar bottom (above Collapse) —
                        a workspace-level destination, not an account action.
                        Integrations lives inside Settings. */}
                    <div className="mx-1 my-0.5 h-px bg-surface-hover" />
                    <button
                      type="button"
                      onClick={() => setWorkspaceSubmenuOpen((o) => !o)}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-1 text-left text-[13px] font-[450] leading-[19.5px] text-[color:var(--text-primary)] transition-colors rounded-lg ${
                        workspaceSubmenuOpen ? 'bg-surface-sunken' : 'hover:bg-surface-sunken'
                      }`}
                      aria-expanded={workspaceSubmenuOpen}
                    >
                      <span>Switch workspaces</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-[color:var(--text-disabled)]" strokeWidth={1.5} aria-hidden />
                    </button>
                    <div className="mx-1 my-0.5 h-px bg-surface-hover" />
                    <button
                      type="button"
                      onClick={(e) => {
                        handleLogout(e);
                        setWorkspaceSubmenuOpen(false);
                        setDropdownOpen(false);
                      }}
                      className="flex w-full items-center justify-between gap-2 px-3 py-1 text-left text-[13px] font-[450] leading-[19.5px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-sunken rounded-lg"
                    >
                      <span>Log out</span>
                      <span className="shrink-0 text-[11px] font-normal tabular-nums text-[color:var(--text-disabled)]">Alt+Shift+Q</span>
                    </button>
                  </nav>
                </div>
                {workspaceSubmenuOpen && teamMenuLayout.workspace ? (
                  <div
                    ref={teamWorkspaceSubmenuRef}
                    className="sidebar-rail-portal scrollbar-thin overflow-hidden rounded-lg border border-line/90 bg-surface p-0.5 shadow-[0_8px_30px_-8px_rgba(15,23,42,0.1)]"
                    style={teamMenuLayout.workspace}
                  >
                    {isLoadingTeams ? (
                      <div className="flex items-center justify-center px-2.5 py-1.5">
                        <p className="text-[12px] text-[color:var(--text-disabled)]">Loading teams…</p>
                      </div>
                    ) : curTeams.length > 0 ? (
                      (() => {
                        const uniqueTeams = Array.from(
                          new Map(curTeams.map((team) => [team.id || team._id, team])).values()
                        );

                        return uniqueTeams.map((team) => {
                          const teamId = team.id || team._id;
                          if (!teamId) {
                            return null;
                          }

                          const planDisplayName = getPlanDisplayName(team.subscription);
                          const isSelected = team.name === selectedTeam;
                          const initial = (team.name || 'T').charAt(0).toUpperCase();

                          return (
                            <button
                              key={teamId}
                              type="button"
                              onClick={() => handleTeamSelect(team)}
                              className={`flex w-full items-center gap-1.5 rounded-md px-2 py-0.5 text-left transition-colors ${
                                isSelected ? 'bg-surface-sunken' : 'hover:bg-surface-sunken/90'
                              }`}
                            >
                              {/* Was a hardcoded #e4eebc: --text-primary is
                                  near-white in dark mode, so the initial sat at
                                  1.09:1 on pale green and vanished. --surface-hover
                                  flips with the theme and matches the chip a few
                                  lines up. */}
                              <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] bg-[color:var(--surface-hover)]">
                                <span className="text-[10px] font-medium leading-none tracking-tight text-[color:var(--text-primary)]">
                                  {initial}
                                </span>
                              </div>
                              <p className="min-w-0 flex-1 truncate text-[13px] font-[450] leading-[19.5px] text-[color:var(--text-primary)]">
                                {planDisplayName ? (
                                  <>
                                    <span className="text-[color:var(--text-disabled)]">{planDisplayName}</span>
                                    <span className="text-[color:var(--border-strong)]" aria-hidden>
                                      {' '}
                                      ·{' '}
                                    </span>
                                    <span>{team.name}</span>
                                  </>
                                ) : (
                                  team.name
                                )}
                              </p>
                              {isSelected && (
                                <Check className="h-3.5 w-3.5 shrink-0 text-[color:var(--text-primary)]" strokeWidth={1.5} aria-hidden />
                              )}
                            </button>
                          );
                        }).filter(Boolean);
                      })()
                    ) : (
                      <div className="flex items-center justify-center px-2.5 py-1.5">
                        <p className="text-center text-[12px] text-[color:var(--text-disabled)]">No teams available</p>
                      </div>
                    )}
                    <div className="mx-1 my-0.5 border-t border-line-subtle" />
                    <button
                      type="button"
                      onClick={goToNewProjectPage}
                      className="flex w-full items-center gap-1.5 rounded-md px-2 py-0.5 text-left text-[13px] font-[450] leading-[19.5px] text-[color:var(--text-muted)] transition-colors hover:bg-surface-sunken hover:text-[color:var(--text-primary)]"
                    >
                      <span className="text-base font-medium leading-none">+</span>
                      <span>New Project</span>
                    </button>
                  </div>
                ) : null}
              </>,
              document.body
            )
          : null}

      <div className={`flex justify-between items-center ${!showExpanded ? "mb-1" : ""} ${!showExpanded ? "py-0.5" : "py-1"} ${!showExpanded ? "justify-center" : ""}`}>
        {showExpanded && <button className="md:hidden text-[color:var(--text-secondary)] hover:text-[color:var(--text-secondary)] p-2" onClick={() => { onClose(); hideMarketing && hideMarketing(); }}>✖</button>}
      </div>

      {/* Demo workspace banner: only while viewing the shared read-only demo. */}
      {isDemoTeamActive() && showExpanded && (
        <div className="mx-2 mb-2 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2.5">
          <div className="text-[12px] font-medium text-[color:var(--text-primary)]">Demo workspace</div>
          <div className="text-[11px] leading-snug text-[color:var(--text-muted)] mt-0.5">
            Read-only sample data. Chat is live.
          </div>
          <button
            type="button"
            onClick={() => exitDemoTeam(navigate, setSelectedTeamContext)}
            className="mt-2 inline-flex h-7 w-full items-center justify-center rounded-md bg-brand text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover"
          >
            Exit demo team
          </button>
        </div>
      )}

      <nav className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin">
        {isAskShell ? (
        <ul className={`${!showExpanded ? "space-y-1.5 px-0 mt-2" : "space-y-px"} sidebar-nav-primary`}>
          <li>
            <button
              type="button"
              onClick={() => {
                let back = null;
                try { back = sessionStorage.getItem('trodo.lastAppPath'); } catch (_) { /* private mode */ }
                navigate(back && back.startsWith(teamBasePath) ? back : `${teamBasePath}/overview`);
              }}
              className={`sidebar-nav-item flex w-full items-center gap-2.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] ${!showExpanded ? "py-0.5 px-2 justify-center" : "py-1 pl-3 pr-2 ml-1 mr-3 rounded-lg"}`}
              style={!showExpanded ? {} : { width: 'calc(100% - 1rem)' }}
              title="Back to app"
            >
              <ChevronLeft size={navIconPx} strokeWidth={1.5} className="sidebar-nav-icon-slot shrink-0 text-[color:var(--text-muted)]" />
              {showExpanded && <span className="text-left text-[13px] font-[450] leading-[19.5px]">Back to app</span>}
            </button>
          </li>
          <li className={showExpanded ? 'pt-2' : ''}>
            <Link
              to={askShellPath(null)}
              title="New chat"
              className={`${!activeSessionId && !isAgentsPage ? "font-medium text-[color:var(--text-primary)] bg-surface-hover" : "text-[color:var(--text-primary)]"} sidebar-nav-item flex items-center gap-2.5 hover:bg-surface-hover ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1 pl-3 pr-2 ml-1 mr-3 rounded-lg"}`}
              style={{ willChange: 'background-color, color', ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' }) }}
            >
              <Plus size={navIconPx} strokeWidth={2} className="sidebar-nav-icon-slot shrink-0" />
              {showExpanded && <span className="flex-1 truncate text-left text-[13px] font-[450] leading-[19.5px]">New chat</span>}
            </Link>
          </li>
          <li>
            <Link
              to={`${teamBasePath}/agents`}
              title="Agents"
              className={`${isAgentsPage ? "font-medium text-[color:var(--text-primary)] bg-surface-hover" : "text-[color:var(--text-primary)]"} sidebar-nav-item flex items-center gap-2.5 hover:bg-surface-hover ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1 pl-3 pr-2 ml-1 mr-3 rounded-lg"}`}
              style={{ willChange: 'background-color, color', ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' }) }}
            >
              <Workflow size={navIconPx} strokeWidth={1.75} className="sidebar-nav-icon-slot shrink-0" />
              {showExpanded && <span className="flex-1 truncate text-left text-[13px] font-[450] leading-[19.5px]">Agents</span>}
            </Link>
          </li>
          {showExpanded ? (
            <li className="flex items-center gap-2 px-4 pb-1 pt-4">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]" aria-hidden>
                Chats
              </span>
              <button
                type="button"
                onClick={() => setChatSearchOpen(true)}
                title="Search all chats"
                aria-label="Search all chats"
                className="ml-auto -mr-1 rounded p-1 text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
              >
                <Search size={13} strokeWidth={2} />
              </button>
            </li>
          ) : (
            <li className="mx-2 my-1.5 border-t border-[color:var(--border-subtle)]" aria-hidden />
          )}
          {showExpanded && askSessions.length === 0 && (
            <li className="px-4 py-1 text-[12px] text-[color:var(--text-disabled)]">No chats yet</li>
          )}
          {showExpanded && askSessions.map((sx) => {
            const active = String(sx.id) === String(activeSessionId);
            return (
              <li key={sx.id}>
                <Link
                  to={askShellPath(sx.id)}
                  title={sx.title || 'Untitled chat'}
                  className={`${active ? "font-medium text-[color:var(--text-primary)] bg-surface-hover" : "text-[color:var(--text-secondary)]"} sidebar-nav-item flex items-center gap-2 py-1 pl-3 pr-2 ml-1 mr-3 rounded-lg hover:bg-surface-hover hover:text-[color:var(--text-primary)]`}
                  style={{ width: 'calc(100% - 1rem)' }}
                >
                  <span className="min-w-0 flex-1 truncate text-left text-[13px] leading-[19.5px]">{sx.title || 'Untitled chat'}</span>
                  <span className="shrink-0 text-[11px] tabular-nums text-[color:var(--text-disabled)]">{relativeShort(sx.updated_at || sx.created_at)}</span>
                </Link>
              </li>
            );
          })}
          {/* Everything the rail's ten do not cover. */}
          <ChatSearchDialog
            open={chatSearchOpen}
            teamId={(() => { try { return JSON.parse(localStorage.getItem('selectedTeamData'))?.id ?? null; } catch (_) { return null; } })()}
            onPick={(id) => navigate(askShellPath(id))}
            onClose={() => setChatSearchOpen(false)}
          />
        </ul>
        ) : isSettingsShell ? (
        <ul className={`${!showExpanded ? "space-y-1.5 px-0 mt-2" : "space-y-px"} sidebar-nav-primary`}>
          <li>
            <button
              type="button"
              onClick={() => navigate(`${teamBasePath}/chat`)}
              className={`sidebar-nav-item flex w-full items-center gap-2.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] ${!showExpanded ? "py-0.5 px-2 justify-center" : "py-1 pl-3 pr-2 ml-1 mr-3 rounded-lg"}`}
              style={!showExpanded ? {} : { width: 'calc(100% - 1rem)' }}
              title="Back to app"
            >
              <ChevronLeft size={navIconPx} strokeWidth={1.5} className="sidebar-nav-icon-slot shrink-0 text-[color:var(--text-muted)]" />
              {showExpanded && <span className="text-left text-[13px] font-[450] leading-[19.5px]">Back to app</span>}
            </button>
          </li>
          {visibleSettingsSections(canPermission).map((section, sectionIdx) => (
            <React.Fragment key={section.title}>
              {/* Expanded: a titled group. Collapsed: labels are hidden, so the
                  grouping reads as a hairline rule between icon clusters. */}
              {showExpanded ? (
                <li
                  className={`px-4 pb-1 ${sectionIdx === 0 ? 'pt-3' : 'pt-4'} text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]`}
                  aria-hidden
                >
                  {section.title}
                </li>
              ) : sectionIdx > 0 ? (
                <li className="mx-2 my-1.5 border-t border-[color:var(--border-subtle)]" aria-hidden />
              ) : null}

              {section.items.map(({ path, label, icon }) => {
                const active = settingsNavIsActive(path, settingsSection);
                const to = `${teamBasePath}/settings/${path}`;
                return (
                  <li key={path}>
                    <NavLink
                      to={to}
                      title={!showExpanded ? label : undefined}
                      className={() =>
                        `${active ? "font-medium text-[color:var(--text-primary)] bg-surface-hover" : "text-[color:var(--text-primary)]"} sidebar-nav-item flex items-center gap-2.5 hover:bg-surface-hover ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1 pl-3 pr-2 ml-1 mr-3 rounded-lg"} cursor-pointer`
                      }
                      style={{
                        willChange: 'background-color, color',
                        ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' }),
                      }}
                    >
                      <span
                        className={`inline-block h-[18px] w-[18px] shrink-0 ${active ? "bg-[color:var(--text-primary)]" : "bg-[color:var(--text-muted)]"}`}
                        style={{
                          WebkitMaskImage: `url(${publicAsset(icon)})`,
                          WebkitMaskSize: 'contain',
                          WebkitMaskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          maskImage: `url(${publicAsset(icon)})`,
                          maskSize: 'contain',
                          maskRepeat: 'no-repeat',
                          maskPosition: 'center',
                        }}
                        aria-hidden
                      />
                      {showExpanded && (
                        <span className="flex-1 truncate text-left text-[13px] font-[450] leading-[19.5px]">{label}</span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </React.Fragment>
          ))}
        </ul>
        ) : (
        <ul className={`${!showExpanded ? "space-y-1.5 px-0 mt-2" : "space-y-px"} sidebar-nav-primary`}>
          <button
            type="button"
            onClick={() => setGlobalSearchOpen(true)}
            title="Search workspace (Ctrl+K)"
            className={`text-[color:var(--text-primary)] sidebar-nav-item flex items-center gap-2.5 hover:bg-surface-hover ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1 pl-3 pr-2 ml-1 mr-3 rounded-lg"}`}
            style={{
              willChange: 'background-color, color',
              ...(!showExpanded ? {} : { width: 'calc(100% - 1rem)' }),
            }}
          >
            <Search size={navIconPx} strokeWidth={2} className="sidebar-nav-icon-slot shrink-0" aria-hidden />
            {showExpanded && (
              <div className="flex items-center justify-between w-full">
                <span className="transition-opacity duration-150 ease-out" style={{ willChange: 'opacity', color: 'var(--text-primary)' }}>
                  Go to...
                </span>
                <kbd className="rounded border border-[color:var(--border-default)] px-1 py-0.5 text-[10px] leading-none font-medium text-[color:var(--text-muted)]">
                  Ctrl K
                </kbd>
              </div>
            )}
          </button>

          {(() => {
            const isActiveItem = isActive("chat");
            return (
              <div
                className={`flex items-center ${!showExpanded ? "w-full" : "ml-1 mr-3"}`}
                style={{ ...(!showExpanded ? {} : { width: "calc(100% - 1rem)" }) }}
              >
                {/* Opens Ask beside the page you are on, carrying that page as
                    context: the common case. On the chat page itself there is
                    nothing to open beside, so this stays a link to it. */}
                {isActiveItem ? (
                  <Link
                    to={`${teamBasePath}/chat`}
                    title="Lucid"
                    className={`font-medium text-[color:var(--text-primary)] bg-surface-hover sidebar-nav-item flex items-center gap-2.5 hover:bg-surface-hover ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1 pl-3 pr-2 rounded-lg flex-1"} cursor-pointer`}
                    style={{ willChange: "background-color, color" }}
                  >
                    <AskIcon size={navIconPx} className="sidebar-nav-icon-slot shrink-0" />
                    {showExpanded && (
                      <span className="transition-opacity duration-150 ease-out" style={{ willChange: "opacity" }}>
                        Lucid
                      </span>
                    )}
                  </Link>
                ) : (
                  <button
                    type="button"
                    title="Lucid (Ctrl I)"
                    onClick={() => window.dispatchEvent(new CustomEvent("trodo:ask:toggle"))}
                    className={`sidebar-ask text-[color:var(--text-primary)] sidebar-nav-item flex items-center gap-2.5 hover:bg-surface-hover ${!showExpanded ? "py-0.5 px-2 justify-center w-full" : "py-1 pl-3 pr-2 rounded-lg flex-1"} cursor-pointer text-left`}
                    style={{ willChange: "background-color, color" }}
                  >
                    <AskIcon size={navIconPx} className="sidebar-nav-icon-slot shrink-0" />
                    {showExpanded && (
                      <span className="transition-opacity duration-150 ease-out" style={{ willChange: "opacity" }}>
                        Lucid
                      </span>
                    )}
                  </button>
                )}
                {isActiveItem && showExpanded && (
                  <button
                    type="button"
                    title="Chat history"
                    onClick={() => window.dispatchEvent(new CustomEvent("trodo:chat:openHistory"))}
                    className="shrink-0 h-7 w-7 flex items-center justify-center rounded-md transition-colors hover:bg-surface-hover"
                    style={{ color: "var(--text-muted)" }}
                  >
                    <Clock size={14} strokeWidth={1.5} />
                  </button>
                )}
              </div>
            );
          })()}

          {/* ─── OBSERVE ─── */}
          {(() => {
            const hasAccess = canAccessPage("activity");
            const subItemCls = (active) =>
              `sidebar-nav-item flex items-center gap-2.5 py-1 hover:bg-surface-hover cursor-pointer ${
                !showExpanded ? "px-2 justify-center w-full" : "pl-3 pr-2 ml-1 mr-3 rounded-lg"
              } ${active ? "font-medium text-[color:var(--text-primary)] bg-surface-hover" : "text-[color:var(--text-primary)]"}`;
            const subItemStyle = { willChange: "background-color, color", ...(!showExpanded ? {} : { width: "calc(100% - 1rem)" }) };

            // While entitlements are still loading we don't yet know whether
            // this team has access — "no access" is what accessiblePages
            // defaults to before the real fetch resolves (subscriptionContext.js),
            // so rendering the locked state here would flash a fake paywall at
            // every fully-entitled user on a fresh page load. Neutral skeleton
            // rows instead: they say "loading", not "you're locked out".
            if (entitlementsLoading) return <NavSectionSkeleton rows={5} showExpanded={showExpanded} />;

            if (!hasAccess) return (
              <div className="text-[color:var(--text-primary)] opacity-50 cursor-not-allowed sidebar-nav-item sidebar-nav-item--locked flex items-center gap-2.5 py-1 px-3 w-full" title="Upgrade your plan to access analytics">
                <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                  <Icon name="agent" size={navIconPx} color="currentColor" />
                </span>
                {showExpanded && <div className="flex items-center justify-between w-full"><span>Observe</span><Lock size={12} strokeWidth={1.25} className="ml-1" /></div>}
              </div>
            );

            return (
              <>
                {showExpanded && (
                  <div className="pt-3 pb-0.5 pl-4 pr-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Observe</span>
                  </div>
                )}
                <Link to={`${teamBasePath}/overview`} className={subItemCls(location.pathname === `${teamBasePath}/overview` || location.pathname.startsWith(`${teamBasePath}/overview/`))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Icon name="overview" size={navIconPx} color="currentColor" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Overview</span>}
                </Link>
                {/* One entry for all three grains of agent telemetry.
                    Conversations contain runs contain spans, so they're tabs on
                    one page rather than separate destinations. Lands on Runs. */}
                <Link
                  to={`${teamBasePath}/agent-analytics/runs`}
                  className={subItemCls(
                    location.pathname.includes("/agent-analytics/runs")
                    || location.pathname.includes("/agent-analytics/traces")
                    || location.pathname.includes("/agent-analytics/conversations")
                  )}
                  style={subItemStyle}
                >
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Icon name="timeline" size={navIconPx} color="currentColor" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Traces</span>}
                </Link>
                <Link to={`${teamBasePath}/activity/users`} className={subItemCls(location.pathname.includes("/activity/users"))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Icon name="user" size={navIconPx} color="currentColor" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Users</span>}
                </Link>
                <Link to={`${teamBasePath}/agent-analytics/capabilities`} className={subItemCls(location.pathname.includes("/agent-analytics/capabilities") || location.pathname.includes("/agent-analytics/use-cases"))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Icon name="usecase" size={navIconPx} color="currentColor" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Capabilities</span>}
                </Link>
              </>
            );
          })()}

          {/* ─── ASSESS ─── */}
          {(() => {
            const hasAccess = canAccessPage("activity");
            const subItemCls = (active) =>
              `sidebar-nav-item flex items-center gap-2.5 py-1 hover:bg-surface-hover cursor-pointer ${
                !showExpanded ? "px-2 justify-center w-full" : "pl-3 pr-2 ml-1 mr-3 rounded-lg"
              } ${active ? "font-medium text-[color:var(--text-primary)] bg-surface-hover" : "text-[color:var(--text-primary)]"}`;
            const subItemStyle = { willChange: "background-color, color", ...(!showExpanded ? {} : { width: "calc(100% - 1rem)" }) };
            // See OBSERVE above: don't collapse to "no access" before we know.
            if (entitlementsLoading) return <NavSectionSkeleton rows={3} showExpanded={showExpanded} />;
            if (!hasAccess) return null;

            return (
              <>
                {showExpanded && (
                  <div className="pt-3 pb-0.5 pl-4 pr-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Assess</span>
                  </div>
                )}
                <Link to={`${teamBasePath}/agent-analytics/evals`} className={subItemCls(location.pathname.includes("/agent-analytics/evals"))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Icon name="eval" size={navIconPx} color="currentColor" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Evals</span>}
                </Link>
                {ISSUES_UI_ENABLED && (
                <Link to={`${teamBasePath}/agent-analytics/issues`} className={subItemCls(location.pathname.includes("/agent-analytics/issues"))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Icon name="issues" size={navIconPx} color="currentColor" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Issues</span>}
                </Link>
                )}
                {/* Alerts sits under Assess, next to Issues: an issue is
                    something Trodo found, an alert is something you asked to
                    be told about. Both are "watch my agents for me". */}
                <Link to={`${teamBasePath}/alerts`} className={subItemCls(location.pathname.includes("/alerts"))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Bell size={navIconPx} strokeWidth={1.75} className="text-current" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Alerts</span>}
                </Link>
                <Link to={`${teamBasePath}/reports`} className={subItemCls(location.pathname.startsWith(`${teamBasePath}/reports`))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Icon name="dashboard" size={navIconPx} color="currentColor" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Reports</span>}
                </Link>
                <Link to={`${teamBasePath}/playground`} className={subItemCls(location.pathname.includes("/playground"))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <FlaskConical size={navIconPx} strokeWidth={1.75} className="text-current" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Playground</span>}
                </Link>
              </>
            );
          })()}

          {/* ─── DEVELOP ─── */}
          {(() => {
            const hasAccess = canAccessPage("activity");
            const subItemCls = (active) =>
              `sidebar-nav-item flex items-center gap-2.5 py-1 hover:bg-surface-hover cursor-pointer ${
                !showExpanded ? "px-2 justify-center w-full" : "pl-3 pr-2 ml-1 mr-3 rounded-lg"
              } ${active ? "font-medium text-[color:var(--text-primary)] bg-surface-hover" : "text-[color:var(--text-primary)]"}`;
            const subItemStyle = { willChange: "background-color, color", ...(!showExpanded ? {} : { width: "calc(100% - 1rem)" }) };

            // See OBSERVE above: don't collapse to "no access" before we know.
            if (entitlementsLoading) return <NavSectionSkeleton rows={4} showExpanded={showExpanded} />;

            if (!hasAccess) return null;

            // Agents is not here any more — it lives inside Ask's agent mode,
            // reached with the toggle on the Ask page. Leaving a second door to
            // it here is what made it feel half-moved.
            return (
              <>
                {showExpanded && (
                  <div className="pt-3 pb-0.5 pl-4 pr-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Develop</span>
                  </div>
                )}
                <Link to={`${teamBasePath}/prompts`} className={subItemCls(location.pathname.includes("/prompts"))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <ScrollText size={navIconPx} strokeWidth={1.75} className="text-current" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Prompts</span>}
                </Link>
                {LEGACY_STUDIO_UI && (
                  <Link to={`${teamBasePath}/analytics/reports`} className={subItemCls(location.pathname.includes("/analytics"))} style={subItemStyle}>
                    <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                      <Icon name="dashboard" size={navIconPx} color="currentColor" />
                    </span>
                    {showExpanded && <span className="transition-opacity duration-150 ease-out">Analytics studio</span>}
                  </Link>
                )}
                <Link to={`${teamBasePath}/datasets`} className={subItemCls(location.pathname.includes("/datasets"))} style={subItemStyle}>
                  <span className="sidebar-nav-icon-slot inline-flex items-center justify-center shrink-0">
                    <Database size={navIconPx} strokeWidth={1.75} className="text-current" />
                  </span>
                  {showExpanded && <span className="transition-opacity duration-150 ease-out">Datasets</span>}
                </Link>
              </>
            );
          })()}
          
        </ul>
        )}
      </nav>

      {/* Bottom: settings, then collapse / expand sidebar */}
      <div className={`mt-auto shrink-0 hidden md:block ${showExpanded ? 'pl-1 pr-3 pb-1.5 pt-1' : 'px-0.5 pb-1.5 pt-1'}`}>
        {/* Already inside Settings? The button would be a link to where you
            are standing: the settings shell's own nav is the way around. */}
        {!isSettingsShell ? (
          <button
            type="button"
            onClick={() => navigate(`${teamBasePath}/settings/project`)}
            title="Settings"
            className={`flex items-center gap-2.5 rounded-lg text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] ${
              showExpanded ? 'w-full py-1 pl-3 pr-2' : 'mx-auto justify-center p-1.5'
            }`}
          >
            <span className="inline-flex items-center justify-center shrink-0">
              <Icon name="sidebar_settings" size={navIconPx} color="currentColor" />
            </span>
            {showExpanded && (
              <span className="text-[13px] font-[450] leading-[19.5px]">Settings</span>
            )}
          </button>
        ) : null}
        <button
          type="button"
          onClick={toggleCollapse}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`flex items-center gap-2.5 rounded-lg text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] ${
            showExpanded ? 'w-full py-1 pl-3 pr-2' : 'mx-auto justify-center p-1.5'
          }`}
        >
          <span className={`inline-flex items-center justify-center shrink-0 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}>
            <Icon name="sidebar_collapsesidebar" size={navIconPx} color="currentColor" />
          </span>
          {showExpanded && (
            <span className="text-[13px] font-[450] leading-[19.5px]">Collapse</span>
          )}
        </button>
      </div>
    </aside>
    {isRefreshing && typeof document !== 'undefined'
      ? createPortal(<WorkspaceSetupOverlay />, document.body)
      : null}
    {!isSettingsShell ? (
      <GlobalSearchModal
        open={globalSearchOpen}
        onClose={() => setGlobalSearchOpen(false)}
        teamSlug={rawTeamSlug}
        boards={LEGACY_STUDIO_UI ? analyticsBoards : []}
        loadBoards={LEGACY_STUDIO_UI ? loadBoards : undefined}
        onOpenRun={(runId, query) => { setSearchPanelRun({ runId, query }); setGlobalSearchOpen(false); }}
        onOpenSpan={(spanId, query) => { setSearchPanelSpan({ spanId, query }); setGlobalSearchOpen(false); }}
        onOpenEvent={(ev) => { setSearchPanelEvent(ev); setGlobalSearchOpen(false); }}
        onOpenUxSignal={(id) => { setSearchPanelUxSignal(id); setGlobalSearchOpen(false); }}
      />
    ) : null}
    {searchPanelRun && typeof document !== 'undefined' ? createPortal(
      <AgentRunDetailPage
        runId={searchPanelRun.runId}
        teamId={searchPanelTeamId}
        initialQuery={searchPanelRun.query || ''}
        onClose={() => setSearchPanelRun(null)}
      />,
      document.body
    ) : null}
    {searchPanelSpan && typeof document !== 'undefined' ? createPortal(
      <TraceDetailPage
        spanId={searchPanelSpan.spanId}
        teamId={searchPanelTeamId}
        highlightQuery={searchPanelSpan.query || ''}
        onClose={() => setSearchPanelSpan(null)}
        onRunClick={(runId) => { setSearchPanelSpan(null); setSearchPanelRun({ runId, query: searchPanelSpan.query }); }}
      />,
      document.body
    ) : null}
    {searchPanelEvent && typeof document !== 'undefined' ? createPortal(
      <SearchEventPanel event={searchPanelEvent} teamId={searchPanelTeamId} onClose={() => setSearchPanelEvent(null)} />,
      document.body
    ) : null}
    {searchPanelUxSignal != null && typeof document !== 'undefined' ? createPortal(
      <UxSignalDetailDrawer
        teamId={searchPanelTeamId}
        uxSignalId={searchPanelUxSignal}
        onClose={() => setSearchPanelUxSignal(null)}
      />,
      document.body
    ) : null}
    </>
  );
};

export default Sidebar;